import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import Button from "components/Button";
import ButtonInverted from "components/ButtonInverted";
import FormMetaStep from "../FormMetaStep";
import FormBuilderStep from "../FormBuilderStep";

const BackDrop = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(56, 57, 61, 0.2);
  z-index: 999;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const Container = styled.div`
  background-color: #fefefe;
  width: min(1100px, calc(100vw - 32px));
  max-height: calc(100vh - 40px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #ececec;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderText = styled.h2`
  font-size: 18px;
  color: #38393d;
  margin: 0;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 24px;
  cursor: pointer;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const ProgressStep = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: ${({ active }) => (active ? "#0073ab" : "#707070")};
  font-weight: ${({ active }) => (active ? 700 : 400)};
  cursor: pointer;
`;

const ProgressLine = styled.div`
  width: 48px;
  height: 1px;
  background: #d9d9d9;
  margin: 0 12px;
`;

const BodyContainer = styled.div`
  padding: 24px;
  overflow-y: auto;
`;

const FooterContainer = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #ececec;
`;

function parseScreeningType(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
}

function EditForm({
  setShowModal,
  bffService,
  buildStructure,
  validateUniqueFieldIds,
  validateFormStructure,
  formValues: initialValues,
  onSuccess,
}) {
  const [formValues, setFormValues] = useState(initialValues);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const canMoveNext = useMemo(
    () => Boolean(formValues.name.trim()) && Boolean(formValues.version.trim()),
    [formValues.name, formValues.version]
  );

  const handleSubmit = async () => {
    const trimmedName = formValues.name.trim();
    const trimmedVersion = formValues.version.trim();
    const nextScreeningType = parseScreeningType(formValues.screeningType);
    const previousScreeningType = parseScreeningType(initialValues.screeningType);

    if (!trimmedName || !trimmedVersion) {
      toast.error("Preencha nome e versão para salvar.");
      return;
    }

    const duplicateFieldIdsError = validateUniqueFieldIds(formValues);
    if (duplicateFieldIdsError) {
      toast.error(duplicateFieldIdsError);
      return;
    }

    const invalidStructureError = validateFormStructure(formValues);
    if (invalidStructureError) {
      toast.error(invalidStructureError);
      return;
    }

    const payload = {};
    const nextStructure = buildStructure(formValues);
    const previousStructure = buildStructure(initialValues);

    if (trimmedName !== initialValues.name) {
      payload.name = trimmedName;
    }

    if ((formValues.description || "") !== (initialValues.description || "")) {
      payload.description = formValues.description.trim() || null;
    }

    if (trimmedVersion !== initialValues.version) {
      payload.version = trimmedVersion;
    }

    if (nextScreeningType !== previousScreeningType) {
      payload.screeningType = nextScreeningType;
    }

    if (JSON.stringify(nextStructure) !== JSON.stringify(previousStructure)) {
      payload.structure = nextStructure;
    }

    if (Object.keys(payload).length === 0 && formValues.isCurrent === initialValues.isCurrent) {
      toast.info("Nenhuma alteração para salvar.");
      setShowModal(false);
      return;
    }

    setSaving(true);

    try {
      if (Object.keys(payload).length > 0) {
        await bffService.updateForm(initialValues.slug, payload);
      }

      if (formValues.isCurrent !== initialValues.isCurrent) {
        await bffService.updateFormStatus(initialValues.slug, formValues.isCurrent);
      }

      toast.success("Formulário atualizado com sucesso!");
      onSuccess();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Falha ao atualizar formulário");
    } finally {
      setSaving(false);
    }
  };

  return (
    <BackDrop>
      <Container>
        <HeaderContainer>
          <HeaderTop>
            <HeaderText>Editar Formulário</HeaderText>
            <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
          </HeaderTop>

          <ProgressContainer>
            <ProgressStep active={currentStep === 1} onClick={() => setCurrentStep(1)}>
              1. Metadados
            </ProgressStep>
            <ProgressLine />
            <ProgressStep active={currentStep === 2} onClick={() => canMoveNext && setCurrentStep(2)}>
              2. Construtor
            </ProgressStep>
          </ProgressContainer>
        </HeaderContainer>

        <BodyContainer>
          {currentStep === 1 ? (
            <FormMetaStep formValues={formValues} setFormValues={setFormValues} />
          ) : (
            <FormBuilderStep formValues={formValues} setFormValues={setFormValues} />
          )}
        </BodyContainer>

        <FooterContainer>
          <ButtonInverted label="Cancelar" onClick={() => setShowModal(false)} />
          {currentStep === 2 ? (
            <ButtonInverted label="Voltar" onClick={() => setCurrentStep(1)} />
          ) : null}
          {currentStep === 1 ? (
            <Button label="Avançar" onClick={() => setCurrentStep(2)} disabled={!canMoveNext} />
          ) : (
            <Button label={saving ? "Salvando..." : "Salvar Alterações"} onClick={handleSubmit} disabled={saving} />
          )}
        </FooterContainer>
      </Container>
    </BackDrop>
  );
}

export default EditForm;
